import {
  Button,
  ButtonsGroup,
  Checkbox,
  Flex,
  Modal,
  ModalProps,
  Space,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUpdateOrganizationSettings, useUserOrganization } from '@modules-deprecated/app/organization'

import { getImgPath } from '../../../../../../utils/getImgPath'
import { useInvoiceEditorForm } from '../../contexts/invoiceEditorFormContext'
import { useInvoiceEditorDocumentType } from '../../hooks/useInvoiceEditorDocumentType'
import * as Styled from './styles'

const confirmationIllustration = getImgPath('/invoice-editor/woman-with-checklist-illustration.svg')

interface ApprovalConfirmationProps extends ModalProps {
  onConfirm: (() => void) | null
}

export const ApprovalConfirmation = ({ onConfirm, ...modalProps }: ApprovalConfirmationProps): ReactElement => {
  const [isProcessing, setIsProcessing] = useState(false)
  const { t } = useTranslation()
  const [shouldNeverShowAgain, setShouldNeverShowAgain] = useState(false)
  const { organization } = useUserOrganization()
  const { update } = useUpdateOrganizationSettings()
  const { trackInvoiceEditor } = useInvoiceEditorForm()
  const { isCreditNote } = useInvoiceEditorDocumentType()

  const { close: closeApprovalConfirmationModal } = useModal(modalProps.id)

  const isModalClosable = !isProcessing

  const updateOrganizationSettings = useCallback(() => {
    if (organization?.id) {
      update({
        key: 'isInvoiceEditorApprovalModalDismissed',
        organizationId: organization?.id,
        value: shouldNeverShowAgain ? '1' : '0',
      })
    }
  }, [organization?.id, update, shouldNeverShowAgain])

  const handleCheckboxChange = useCallback(() => {
    setShouldNeverShowAgain((prevState) => !prevState)
    trackInvoiceEditor('Approval confirmation modal - Should never show again checkbox clicked')
  }, [trackInvoiceEditor])

  const handleCancelButtonClick = useCallback(() => {
    trackInvoiceEditor('Approval confirmation modal - Continue editing button clicked')
    closeApprovalConfirmationModal()
  }, [closeApprovalConfirmationModal, trackInvoiceEditor])

  const handleApproveButtonClick = useCallback(() => {
    setIsProcessing(true)
    trackInvoiceEditor('Approval confirmation modal - Confirm button clicked')
    updateOrganizationSettings()

    if (onConfirm) {
      onConfirm()
    }
  }, [onConfirm, trackInvoiceEditor, updateOrganizationSettings])

  useEffect(() => {
    trackInvoiceEditor('Approval confirmation modal opened')
  }, [trackInvoiceEditor])

  useEffect(() => {
    return () => {
      closeApprovalConfirmationModal()
    }
  }, [closeApprovalConfirmationModal])

  return (
    <Modal {...modalProps} size="s" closable={isModalClosable}>
      <Modal.Header>
        <Flex justifyContent="center" width="100%">
          <Text variant="h2">{t('external_invoices.editor.confirm_approval_modal.title')}</Text>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <Flex flexDirection="column" alignItems="center" width="100%">
          <Space size="s6" />
          <img alt="Invoice editor finalization illustration" height={144} src={confirmationIllustration} width={146} />
          <Space />
          <Text alignment="center" weight="bold">
            {t(
              isCreditNote
                ? 'external_invoices.editor.confirm_approval_modal.warning.credit_note'
                : 'external_invoices.editor.confirm_approval_modal.warning',
            )}
          </Text>
          <Space size="s4" />
          <Text colorVariant="secondary">{t('external_invoices.editor.confirm_approval_modal.description')}</Text>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Styled.FooterContentWrapper>
          <Flex justifyContent="space-between" alignItems="center" flexGrow={1}>
            <Checkbox checked={shouldNeverShowAgain} disabled={isProcessing} onChange={handleCheckboxChange}>
              <Text>{t('external_invoices.editor.confirm_approval_modal.dont_show_again')}</Text>
            </Checkbox>
            <ButtonsGroup>
              <Button disabled={isProcessing} variant="text" onClick={handleCancelButtonClick}>
                {t('external_invoices.editor.confirm_approval_modal.continue_editing')}
              </Button>
              <Button loading={isProcessing} onClick={handleApproveButtonClick}>
                {t(
                  isCreditNote
                    ? 'external_invoices.editor.confirm_approval_modal.approve_credit_note'
                    : 'external_invoices.editor.confirm_approval_modal.approve_invoice',
                )}
              </Button>
            </ButtonsGroup>
          </Flex>
        </Styled.FooterContentWrapper>
      </Modal.Footer>
    </Modal>
  )
}

export const ApprovalConfirmationModal = withModalConditionalRender(ApprovalConfirmation)
